import { apiClient } from "../api/apiClient.js";

// Thin fetch wrappers over the Prompt 108 Backoffice Worker Assignment API.
// Every query param is optional - visibility scoping (Agent/PLC/SuperAgent/
// SuperAdmin) always happens on the backend from the caller's own session,
// never from a value this file sends. This file only ever calls the four
// real P108 routes (list assignments, one assignment's detail, create
// assignment, cancel assignment) - it never calls a tracking/device/
// geofence/Invite-Card API (none exist anywhere in this codebase), and
// workerId is only ever sent as a write from createAssignment's own request
// body - no other function in this file accepts or forwards a workerId as a
// write.

const ASSIGNMENTS_PATH = "/backoffice/thailand-jobs/assignments";

// Exactly the param names BackofficeAssignmentsController.ListAssignments
// declares, in the same order as the controller signature - anything else
// passed to listAssignments is silently dropped rather than forwarded, so a
// typo'd key (or a future filter added to the UI without updating this
// whitelist) can never accidentally reach the backend as an unfiltered
// pass-through.
const ASSIGNMENT_LIST_PARAM_KEYS = [
  "page",
  "pageSize",
  "search",
  "assignmentStatus",
  "superAgentOrganizationId",
  "plcOrganizationId",
  "agentId",
  "employerId",
  "jobId",
  "workerId",
];

/** Builds a query string with every empty/null/undefined value omitted entirely (never sent as `key=`), restricted to `paramKeys`. */
function buildQuery(paramKeys, params = {}) {
  const searchParams = new URLSearchParams();
  for (const key of paramKeys) {
    const value = params[key];
    if (value === null || value === undefined || value === "") {
      continue;
    }
    searchParams.set(key, String(value));
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function listAssignments(params = {}) {
  return apiClient.get(`${ASSIGNMENTS_PATH}${buildQuery(ASSIGNMENT_LIST_PARAM_KEYS, params)}`);
}

// Detail fetch requires a real, non-empty assignmentId - this file never
// issues a request for a missing/blank id (the caller only calls this once
// a row/card is actually clicked).
export function getAssignment(assignmentId) {
  if (!assignmentId) {
    throw new Error("getAssignment requires an assignmentId");
  }
  return apiClient.get(`${ASSIGNMENTS_PATH}/${encodeURIComponent(assignmentId)}`);
}

// request is the exact CreateAssignmentRequest shape (jobId, workerId,
// assignmentNote, expectedStartDate) - the only place in this whole
// codebase's frontend that ever sends a workerId as a write. No other
// function in this file, and no other page's create/edit form, ever
// includes a workerId field.
export function createAssignment(request) {
  return apiClient.post(ASSIGNMENTS_PATH, request);
}

// request is the exact CancelAssignmentRequest shape (cancelReason only).
// This is a status change (Active -> Cancelled), never a delete - the real
// endpoint (POST .../assignments/{assignmentId}/cancel) never removes the
// row, and this file has no delete/remove function for assignments at all.
export function cancelAssignment(assignmentId, request) {
  if (!assignmentId) {
    throw new Error("cancelAssignment requires an assignmentId");
  }
  return apiClient.post(`${ASSIGNMENTS_PATH}/${encodeURIComponent(assignmentId)}/cancel`, request);
}
